import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { Link, useOutletContext } from 'react-router-dom';
import { GenerationKind, GenerationStatus } from '@courseai/domain';
import type { ProjectTabContext } from '../ProjectDetail';
import { useT, type I18n, type TranslationKey } from '../../i18n';
import {
  MY_GENERATION_REQUESTS,
  type GenerationRequestView,
  type MyGenerationRequestsData,
  type MyGenerationRequestsVariables,
} from '../../graphql/generations';

// A draft's derived lifecycle stage, computed from its AGENT_COURSE generation
// request. A "draft" is an AI course generation that has not become a finished
// course yet: still proposing, awaiting review/approval, generating scripts, or
// failed. Once fully generated it appears as a normal course in the Courses tab.
type DraftStage = 'proposing' | 'awaitingReview' | 'generating' | 'failed';

const DRAFT_STAGE_KEYS: Record<DraftStage, TranslationKey> = {
  proposing: 'drafts.stage.proposing',
  awaitingReview: 'drafts.stage.awaitingReview',
  generating: 'drafts.stage.generating',
  failed: 'drafts.stage.failed',
};

// Derive the display stage of an AGENT_COURSE request:
//  - PENDING/RUNNING          → proposing (agent researching a structure)
//  - READY without a courseId → awaitingReview (proposal ready to approve)
//  - READY with a courseId    → generating (approved; scripts/media in progress)
//  - FAILED                   → failed
export function draftStage(request: GenerationRequestView): DraftStage {
  if (request.status === GenerationStatus.FAILED) {
    return 'failed';
  }
  if (request.status === GenerationStatus.READY) {
    return request.courseId === null || request.courseId === undefined
      ? 'awaitingReview'
      : 'generating';
  }
  return 'proposing';
}

function draftStageLabel(t: I18n['t'], stage: DraftStage): string {
  return t(DRAFT_STAGE_KEYS[stage]);
}

// Drafts tab: lists the project's in-progress AI course generations (drafts).
// Each row links to the draft detail view, which shows the current state and
// what work is in progress, and — when a proposal is awaiting review — lets the
// creator review/edit and approve it into a course.
export function DraftsTab(): ReactNode {
  const { projectId } = useOutletContext<ProjectTabContext>();
  const { t } = useT();

  const { data, loading, error, refetch } = useQuery<
    MyGenerationRequestsData,
    MyGenerationRequestsVariables
  >(MY_GENERATION_REQUESTS, {
    variables: { projectId },
    skip: projectId === '',
    fetchPolicy: 'cache-and-network',
  });

  // Only AGENT_COURSE requests are drafts (episode audio/video requests are
  // progress on an already-created course, tracked in the draft detail and the
  // Generations tab). Newest first by updatedAt.
  const drafts = useMemo<ReadonlyArray<GenerationRequestView>>(() => {
    const all = data?.myGenerationRequests ?? [];
    return [...all]
      .filter((request) => request.kind === GenerationKind.AGENT_COURSE)
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : a.updatedAt > b.updatedAt ? -1 : 0));
  }, [data]);

  return (
    <div className="tab-panel">
      <div className="tab-panel-header">
        <h3>{t('drafts.title')}</h3>
        <button type="button" onClick={() => void refetch()}>
          {t('drafts.refresh')}
        </button>
      </div>

      {error && <p className="form-error">{t('drafts.loadError', { message: error.message })}</p>}

      {loading && drafts.length === 0 ? (
        <p className="tab-placeholder">{t('drafts.loading')}</p>
      ) : drafts.length === 0 ? (
        <p className="tab-placeholder">{t('drafts.empty')}</p>
      ) : (
        <ul className="draft-list">
          {drafts.map((draft) => {
            const stage = draftStage(draft);
            return (
              <li key={draft.generationRequestId} className="draft-row">
                <Link
                  to={`/projects/${projectId}/drafts/${draft.generationRequestId}`}
                  className="draft-row-link"
                >
                  <span className="draft-row-date">
                    {new Date(draft.updatedAt).toLocaleString()}
                  </span>
                  <span className={`draft-stage draft-stage-${stage}`}>
                    {draftStageLabel(t, stage)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
